import * as React from "react";
import { InscriptionSuspensionApi, InscriptionSuspensionDto } from "../../../../../../api";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import * as Yup from "yup";
import { ETLCodes } from "../../../../../../locales";
import styled from "styled-components";
import { Classes, Dialog } from "@blueprintjs/core";
import { FGWalterDateMaskInput, SmallFormGenerator } from "../../../../../../components";
import { FGTextAreaInput, FieldGroup } from "nsitools-react";

const StyledDialog = styled(Dialog)`
  width: 500px;
  height: auto;
  background-color: white;
`;

export interface IInscriptionSuspensionDetailDialogProps {
  dialogOpen: boolean;
  onClose: (toSave?: InscriptionSuspensionDto) => void;
  currentSuspension: InscriptionSuspensionDto;
}

export const InscriptionSuspensionDetailDialog: React.FunctionComponent<IInscriptionSuspensionDetailDialogProps> = ({
  currentSuspension,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(InscriptionSuspensionApi);

  const { data, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => currentSuspension,
        saveApiFn: d => api.inscriptionSuspensionSaveInscriptionSuspension({ InscriptionSuspensionDto: d }),
        onSaved: d => onClose(d),
        serverValidationRootKey: "Dto"
      }),
      [api, currentSuspension, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          dateDebut: Yup.date()
            .nullable()
            .when("dateFin", (value, schema) =>
              !!value ? schema.max(Yup.ref("dateFin"), t(ETLCodes.DateDebutGreaterThanDateFin)) : schema
            )
            .required(t(ETLCodes.Required)),
          dateFin: Yup.date()
            .nullable()
            .when("dateFin", (value, schema) =>
              !!value ? schema.min(Yup.ref("dateDebut"), t(ETLCodes.DateFinGreaterThanDateDebut)) : schema
            )
        },
        [["dateFin", "dateFin"]]
      ),
    [t]
  );

  return (
    <StyledDialog title={t(ETLCodes.SuspensionInscription)} isOpen={dialogOpen} onClose={() => onClose()}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose()}
          showDeleteButton={false}
          saving={saving}
          minLabelWidth={100}
          validationErrors={validationErrors}
          preventDefaultSaveOnEnter
        >
          <FieldGroup>
            <FGWalterDateMaskInput name="dateDebut" label={t(ETLCodes.DateDebut)} />
            <FGWalterDateMaskInput name="dateFin" label={t(ETLCodes.DateFin)} />
            <FGTextAreaInput name="remarque" label={t(ETLCodes.Remarque)} />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
